import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@app/services/auth.service';
import { LicenseService } from '@app/services/license.service';
import { AuthenticationResult } from '@azure/msal-browser';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private licenseService: LicenseService, private authService: AuthService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const scopes = this.authService.getScopesForEndpoint(req.url);

    if (!scopes) {
      return next.handle(req); // The resource being requested is not in the MSAL endpoints and we should treat it as a normal request.
    }

    // if the user is not authenticated then drop the request
    if (!this.authService.accountInfo) {
      return throwError('Cannot send request to registered endpoint if the user is not authenticated.');
    }

    const attachToken$ = mergeMap((result: AuthenticationResult) => {
      const authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${result.accessToken}`)
      });
      // send cloned request with header to the next handler.
      return next.handle(authReq);
    });

    const acquireTokenWithFallback$ = this.authService.useRedirect ?
      this.authService.acquireTokenSilentWithFallBackToRedirect({ ...scopes, ...{ account: this.authService.accountInfo } }, scopes)
      :
      this.authService.acquireTokenSilentWithFallBackToPopup({ ...scopes, ...{ account: this.authService.accountInfo } }, scopes);

    const acquireTokenAndContinue$ = acquireTokenWithFallback$
      .pipe(
        attachToken$,
        catchError((err) => {
          if (err?.status === 401 || err?.name === 'ClientAuthError') {
            // We are unauthenticated, try again
            const acquireToken$ = this.authService.useRedirect ? this.authService.acquireTokenRedirect(scopes) : this.authService.acquireTokenPopup(scopes);
            return acquireToken$.pipe(attachToken$);
          }
          return throwError(err);
        }));

    if (req.url.indexOf('sharepoint.com') === -1) {
      return acquireTokenAndContinue$;
    } else {
      // Do license check for 365 when doing calls to sharepoint and return unlicensed error if no valid license.
      // We do this to catch WP Light users.
      // NOTE: DO NOT DO THIS CHECK FOR CALLS TO THE GRAPH API (ME-ENDPOINT) SINCE THAT COULD TRIGGER AN ENDLESS LOOP
      return this.licenseService.hasUserAny365License()
        .pipe(
          mergeMap(hasLicense => {
            if (hasLicense) {
              return acquireTokenAndContinue$;
            } else {
              // We return 402 Payment Required when we're not licensed to access a sharepoint api - https://tools.ietf.org/html/rfc7231#section-6.5.2
              return throwError(new HttpErrorResponse({ status: 402, statusText: 'Unlicensed', url: req.url }));
            }
          })
        );
    }
  }
}
